import { Autocomplete as AutocompleteMUI, CircularProgress, ListItem, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_END_OFFSET = 48;
const DEFAULT_LOAD_MORE_DELAY = 300;

const getSafeLabel = (option, getOptionLabel, labelKeys) => {
  if (typeof getOptionLabel === "function") {
    return getOptionLabel(option);
  }

  if (option == null) return "";
  if (typeof option === "string" || typeof option === "number") return String(option);

  if (Array.isArray(labelKeys) && labelKeys.length) {
    for (const key of labelKeys) {
      if (option?.[key]) return String(option[key]);
    }
  }

  const commonKeys = ["name", "fullName", "label", "title", "code"];
  for (const key of commonKeys) {
    if (option?.[key]) return String(option[key]);
  }

  for (const key of Object.keys(option)) {
    const value = option[key];
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
  }

  return "";
};

const mergeInfinityLoadingOption = (filteredOptions, options) => {
  const hasInfinityLoadingRow = options.some((option) => option?.infinityLoading);
  if (!hasInfinityLoadingRow) return filteredOptions;

  const normalizedOptions = filteredOptions.filter((option) => !option?.infinityLoading);
  const infinityLoadingOption = options.find((option) => option?.infinityLoading);
  return infinityLoadingOption ? normalizedOptions.concat(infinityLoadingOption) : normalizedOptions;
};

/**
 * Wrapper cho MUI Autocomplete với row loading ở cuối danh sách khi isInfinityLoading + loading = true.
 * Logic load thêm dữ liệu (scroll / phân trang) sẽ được handle ở bên ngoài component này.
 */
const AutoComplete = ({
  isInfinityLoading,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  endReachedOffset = DEFAULT_END_OFFSET,
  loadMoreDelay = DEFAULT_LOAD_MORE_DELAY,
  renderOption,
  options = [],
  loading,
  getOptionLabel,
  labelKeys,
  getEndOffset,
  ListboxProps: listboxPropsFromUser,
  filterOptions,
  ...props
}) => {
  const requestedMoreRef = useRef(false);
  const loadMoreTimeoutRef = useRef(null);
  const [isWaitingLoadMore, setIsWaitingLoadMore] = useState(false);
  const showInfinityRow = Boolean((isInfinityLoading && loading) || (hasMore && (loadingMore || isWaitingLoadMore)));

  useEffect(() => {
    if (!loadingMore) {
      requestedMoreRef.current = false;
    }
  }, [loadingMore, options.length]);

  useEffect(() => {
    if (loadingMore || !hasMore) {
      setIsWaitingLoadMore(false);
    }
  }, [loadingMore, hasMore]);

  useEffect(() => {
    setIsWaitingLoadMore(false);
  }, [options.length]);

  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, []);

  const optionsWithInfinityLoading = showInfinityRow ? options.concat({ infinityLoading: true, _id: "infinityLoading" }) : options;

  const safeGetOptionLabel = useMemo(() => (option) => getSafeLabel(option, getOptionLabel, labelKeys), [getOptionLabel, labelKeys]);
  const mergedFilterOptions = useMemo(
    () => (currentOptions, state) => {
      const optionsWithoutInfinityLoading = currentOptions.filter((option) => !option?.infinityLoading);
      const filteredOptions = typeof filterOptions === "function" ? filterOptions(optionsWithoutInfinityLoading, state) : optionsWithoutInfinityLoading;
      return mergeInfinityLoadingOption(filteredOptions, currentOptions);
    },
    [filterOptions],
  );

  const mergedRenderOption =
    !isInfinityLoading && !hasMore && renderOption
      ? renderOption
      : (listItemProps, option, state, ownerState) => {
          if (option?.infinityLoading) {
            return (
              <ListItem
                {...listItemProps}
                sx={{ "&:hover": { backgroundColor: "transparent !important" } }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  width="100%"
                >
                  <CircularProgress size={16} />
                </Stack>
              </ListItem>
            );
          }

          if (renderOption) {
            return renderOption(listItemProps, option, state, ownerState);
          }

          return (
            <ListItem {...listItemProps}>
              <Typography variant="body2">{safeGetOptionLabel(option)}</Typography>
            </ListItem>
          );
        };

  return (
    <AutocompleteMUI
      {...props}
      loading={loading}
      options={optionsWithInfinityLoading}
      filterOptions={mergedFilterOptions}
      getOptionLabel={safeGetOptionLabel}
      renderOption={mergedRenderOption}
      ListboxProps={{
        ...listboxPropsFromUser,
        sx: [{ maxHeight: "200px" }, listboxPropsFromUser?.sx],
        onScroll: (event) => {
          listboxPropsFromUser?.onScroll?.(event);
          const target = event.currentTarget;
          const { scrollTop, scrollHeight, clientHeight } = target;
          const offset = scrollHeight - clientHeight - scrollTop;

          if (typeof getEndOffset === "function") {
            if (isInfinityLoading) {
              getEndOffset(offset, { scrollTop, scrollHeight, clientHeight, event });
            }
          }

          if (!hasMore || loadingMore || typeof onLoadMore !== "function") return;

          if (offset > endReachedOffset) {
            requestedMoreRef.current = false;
            setIsWaitingLoadMore(false);
            if (loadMoreTimeoutRef.current) {
              clearTimeout(loadMoreTimeoutRef.current);
              loadMoreTimeoutRef.current = null;
            }
            return;
          }

          if (requestedMoreRef.current) return;

          requestedMoreRef.current = true;
          setIsWaitingLoadMore(true);
          if (loadMoreTimeoutRef.current) {
            clearTimeout(loadMoreTimeoutRef.current);
          }

          loadMoreTimeoutRef.current = setTimeout(() => {
            onLoadMore({
              distanceToBottom: offset,
              scrollTop,
              scrollHeight,
              clientHeight,
              event,
            });
            loadMoreTimeoutRef.current = null;
          }, loadMoreDelay);
        },
      }}
    />
  );
};

export default AutoComplete;
